import React, { useCallback } from 'react';

import { createPortal } from 'react-dom';

import { Editor, type IAllProps } from '@tinymce/tinymce-react';
import { unescape } from 'html-escaper';

import type { SelectOption } from '@/_components';
import { Spinner } from '@/_components';
import { noop, snackbar, useHighlightedText, useTheme } from '@/_shared';

type TinyEditor = Parameters<Required<IAllProps>['onEditorChange']>[1];

type NestedMenuItemContents = {
  type: 'menuitem';
  text: string;
  onAction: () => void;
};

export type TinymceProps = {
  connectedApiKey: string;
  initialValue?: string;
  value?: string;
  height: number;
  min_height?: number;
  max_height?: number;
  placeholder?: string;
  autoResize?: boolean;
  allowImageUpload?: boolean;
  disabled?: boolean;
  fileSizeLimit?: number;
  isUploadingFile?: boolean;
  inline?: boolean;
  autoFocus?: boolean;
  bracesListOptions?: SelectOption<string>[];
  enableSearch?: boolean; // New prop to enable/disable search; Default value is false
  messengerMode?: boolean; // Enable messenger styling (toolbar at bottom, no box shadow)
  readOnly?: boolean;
  cloudChannel?: IAllProps['cloudChannel'];
  onChange: (txt: string) => void;
  onLoadContent: IAllProps['onLoadContent'];
  onFileChange?: (file: File) => void;
  onFocus?: (isFocused: boolean) => void;
};

export function Tinymce({
  connectedApiKey,
  initialValue = '',
  value = '',
  height,
  min_height,
  max_height,
  placeholder = 'Write your question here.',
  autoResize = false,
  allowImageUpload = false,
  disabled = false,
  fileSizeLimit = 6,
  isUploadingFile = false,
  inline = false,
  autoFocus = false,
  bracesListOptions,
  enableSearch = false,
  messengerMode = false,
  readOnly = false,
  cloudChannel = '8',
  onChange,
  onLoadContent,
  onFileChange = noop,
  onFocus = noop,
}: TinymceProps) {
  const { mode } = useTheme();
  const lastEmittedValue = React.useRef(value);

  const nValue = useHighlightedText(value);
  const enableVariableList = !!bracesListOptions;
  const isReadOnly = disabled || readOnly;

  // If the value matches what we just emitted, use it directly to avoid unescaping
  // which can cause Tinymce to see a difference and reset the cursor.
  const isValueEquivalent = lastEmittedValue.current === value;
  const rawValue = isValueEquivalent ? value : unescape(value);
  const editorValue = enableVariableList && isReadOnly ? nValue : rawValue;

  const imageFilePicker = React.useCallback(
    (cb: (value: string, meta?: Record<string, unknown> | undefined) => void) => {
      const input = document.createElement('input');
      const ui = document.getElementsByClassName('tox-dialog-wrap');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', '.jpg, .jpeg, .png, .gif');

      input.onchange = async () => {
        const file = input.files?.[0];

        if (file && file.type.indexOf('image') === 0) {
          if (file.size / 1024 / 1024 > fileSizeLimit) {
            snackbar.error(`Please upload an image of less than ${fileSizeLimit}MBs.`);
            return;
          }
          for (let i = 0; i < ui.length; i++) {
            ui[i].setAttribute('style', 'display: none;');
          }

          const result = await onFileChange(file);
          if (typeof result === 'string') {
            for (let i = 0; i < ui.length; i++) {
              ui[i].setAttribute('style', 'display: null;');
            }

            cb(result, { title: file.name });
          }
        } else {
          snackbar.error("Couldn't pick file, please select appropriate image file.");
        }
      };

      input.click();
    },
    [onFileChange, fileSizeLimit]
  );

  const handleOpenEditorDialog = useCallback(() => {
    try {
      const uploadFileDialogElement = document.getElementsByClassName('tox-dialog')[0];
      const inputs = uploadFileDialogElement.getElementsByTagName('input');

      if (inputs) {
        for (let i = 0; i < inputs.length; i++) {
          inputs[i].setAttribute('disabled', 'true');
        }
      }

      if (allowImageUpload) {
        uploadFileDialogElement.getElementsByClassName('tox-dialog__title')[0].innerHTML = 'Insert/Edit File';
      }
    } catch (e) {
      console.error("Couldn't update dialog title", e);
    }
  }, [allowImageUpload]);

  const onEditorSetup = useCallback(
    (editor: TinyEditor) => {
      if (bracesListOptions) {
        editor.ui.registry.addMenuButton('wrapBraces', {
          text: '{..}',
          tooltip: 'Insert placeholder',
          search: enableSearch ? { placeholder: 'Search...' } : false,
          fetch: (callback: (items: NestedMenuItemContents[]) => void, fetchContext: { pattern: string }) => {
            const items: NestedMenuItemContents[] = bracesListOptions.map((item) => ({
              type: 'menuitem',
              text: item.name,
              onAction: () => editor.insertContent(item.value),
            }));
            if (enableSearch && fetchContext.pattern.length > 0) {
              const filteredItems = items.filter((item) =>
                item.text.toLowerCase().includes(fetchContext.pattern.toLowerCase())
              );
              if (filteredItems.length > 0) {
                callback(filteredItems);
              } else {
                callback([{ type: 'menuitem', text: 'No results found', onAction: noop }]);
              }
            } else {
              callback(items);
            }
          },
        });
      }
    },
    [bracesListOptions, enableSearch]
  );

  const editorConfig: IAllProps['init'] = React.useMemo(() => {
    return {
      height,
      forced_root_block: 'div',
      min_height: min_height ?? height,
      max_height,
      paste_as_text: true,
      menubar: false,
      plugins: `lists link${autoResize ? ' autoresize' : ''}${allowImageUpload ? ' image' : ''}`,
      toolbar: `undo redo | bold italic underline strikethrough | numlist bullist | indent outdent${
        allowImageUpload ? ' | image' : ''
      } ${enableVariableList ? ' | wrapBraces' : ''}`,
      ...(messengerMode && { toolbar_location: 'bottom' }),
      contextmenu: 'copy paste | link',
      placeholder,
      automatic_uploads: false,
      file_picker_types: 'image',
      images_file_types: 'jpg,jpeg,png,gif',
      file_picker_callback: imageFilePicker,
      browser_spellcheck: !isReadOnly,
      ...(autoFocus && { auto_focus: true }),
      init_instance_callback(editor: TinyEditor) {
        editor.on('OpenWindow', handleOpenEditorDialog);
      },
      skin: mode === 'dark' ? 'oxide-dark' : 'oxide',

      setup: onEditorSetup,
      ...(messengerMode && {
        content_css: `
          .tox .tox-toolbar {
            box-shadow: none !important;
            border-top: 1px solid ${mode === 'dark' ? '#374151' : '#e5e7eb'};
            background: transparent;
          }
          .tox .tox-editor-container {
            border: none !important;
          }
          .tox .tox-toolbar__primary {
            background: transparent;
          }
        `,
      }),
    };
  }, [
    height,
    isReadOnly,
    autoResize,
    allowImageUpload,
    enableVariableList,
    placeholder,
    autoFocus,
    imageFilePicker,
    mode,
    messengerMode,
    onEditorSetup,
    handleOpenEditorDialog,
    max_height,
    min_height,
  ]);

  const handleEditorChange = React.useCallback<Required<IAllProps>['onEditorChange']>(
    (str, editor) => {
      if ((editor.isNotDirty && value.length > 0) || isReadOnly) {
        return;
      }

      lastEmittedValue.current = str;
      onChange(str);
    },
    [isReadOnly, onChange, value.length]
  );

  const handleFocusIn = React.useCallback(() => {
    onFocus(true);
  }, [onFocus]);

  const handleFocusOut = React.useCallback(() => {
    onFocus(false);
  }, [onFocus]);

  return (
    <>
      <Editor
        apiKey={connectedApiKey}
        cloudChannel={cloudChannel}
        initialValue={unescape(initialValue)}
        value={editorValue}
        init={editorConfig}
        onEditorChange={handleEditorChange}
        disabled={disabled}
        readonly={readOnly}
        onLoadContent={onLoadContent}
        inline={inline}
        onFocus={handleFocusIn}
        onBlur={handleFocusOut}
      />

      {isUploadingFile &&
        createPortal(
          <div className="bg-accent-light/50 fixed top-0 right-0 bottom-0 left-0 z-9999 flex items-center justify-center">
            <Spinner />
          </div>,
          document.body
        )}
    </>
  );
}
